
import React from 'react';
import { MailIcon, ClockIcon, MapPinIcon } from './Icons';
import { Page } from '../types';

interface FooterProps {
  navigateTo: (page: Page, anchor?: string) => void;
  isCompact?: boolean;
}

const FooterButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200 text-left">
    {children}
  </button>
);

const Footer: React.FC<FooterProps> = ({ navigateTo, isCompact }) => {
  return (
    <footer className="bg-[image:var(--footer-bg)] border-t border-[var(--border-color)] text-[var(--text-secondary)]">
      <div className={`container mx-auto px-4 sm:px-6 lg:px-8 py-16 ${isCompact ? 'hidden' : ''}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Column 1: Brand */}
          <div className="space-y-4">
            <div>
                <button onClick={() => navigateTo('home')} className="flex items-center space-x-2">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[var(--gradient-from)]">
                      <path d="M12 2L2 7V17L12 22L22 17V7L12 2ZM12 12.33L5.5 8.67L12 5L18.5 8.67L12 12.33ZM12 14.5L18.5 10.83V16.33L12 20V14.5ZM5.5 10.83L12 14.5V20L5.5 16.33V10.83Z" fill="url(#logoGradientFooter)"/>
                      <defs>
                        <linearGradient id="logoGradientFooter" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0%" stopColor="var(--gradient-from)" />
                          <stop offset="100%" stopColor="var(--gradient-to)" />
                        </linearGradient>
                      </defs>
                    </svg>
                  <span className="text-xl font-bold text-[var(--text-primary)]">Codemine</span>
                </button>
                <p className="text-sm italic text-[var(--text-secondary)] opacity-90 mt-1">
                    Ideas Mined to Perfection
                </p>
            </div>
            <p className="text-sm pt-2">
              The future of web development, powered by AI. Build, iterate, and deploy faster than ever before.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Quick Links</h3>
            <ul className="space-y-2">
              <li><FooterButton onClick={() => navigateTo('home')}>Home</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('about')}>About Us</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('templates')}>Templates</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('pricing')}>Pricing</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('ai_agents')}>AI Agents</FooterButton></li>
            </ul>
          </div>

          {/* Column 3: Support */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Support</h3>
            <ul className="space-y-2">
              <li><FooterButton onClick={() => navigateTo('terms')}>Terms & Conditions</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('privacy')}>Privacy Policy</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('faq')}>FAQ</FooterButton></li>
              <li><FooterButton onClick={() => navigateTo('contact')}>Contact Us</FooterButton></li>
            </ul>
          </div>

          {/* Column 4: Contact Us */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MailIcon />
                <a href="mailto:hellocodemine@yahoo.com" className="hover:text-[var(--text-primary)] transition-colors">hellocodemine@yahoo.com</a>
              </li>
              <li className="flex items-start space-x-3">
                <ClockIcon />
                <span>Office Hours: 9am - 5pm</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPinIcon />
                <span>Faisalabad, Punjab, Pakistan</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--border-color)] py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm">
          <p>
            &copy; {new Date().getFullYear()} Codemine. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
